// Sync-issues / docs-health derivation hooks (RFC-0009 Task 3.1; PD-0006).
//
// This RFC adds NO API surface — the sync-issues view is derived entirely from
// RFC-0006's read endpoints. `GET /api/projects` list items already carry every
// field a row needs (`manifestBroken`, `manifestError`, `lastSyncError`,
// `manifestBrokenAt`, `autoIndexed`, `stale`, `lastSyncedAt`), so rows are built
// client-side with no per-repo detail fetch.
//
// Fetch strategy (RFC-0009 §3):
//   - A single *server-filterable* unhealthy kind (broken_manifest / failed_sync
//     / stale) narrows server-side via RFC-0006's boolean filters.
//   - The `auto_indexed` kind (a per-item field, NOT a filter) and the default
//     all-kinds view fetch the list UNFILTERED and derive rows client-side.
//   - Manifest-adoption % is the share of listed repos with `autoIndexed: false`,
//     computed from that same UNFILTERED full-list fetch — so it shares the
//     TanStack Query cache with the all-kinds view and costs no extra request.
//
// The raw `ProjectListItem[]` is cached under a filter-keyed query and the two
// consumers (`useSyncIssues`, `useManifestAdoption`) transform it via `select`,
// so the unfiltered fetch is de-duplicated across both. Keys live under
// `['sync-issues']` so RFC-0002's connect success (which invalidates that family)
// refreshes the view.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ProjectListItem } from '@portal/shared-types';
import { apiGet, type QueryParams } from '@/lib/api-client';
import type { Paginated } from '@/lib/queries/projects';

/** The four issue kinds the sync-issues view surfaces (RFC-0009 §3). */
export type SyncIssueKind = 'broken_manifest' | 'failed_sync' | 'stale' | 'auto_indexed';

/**
 * A single derived issue row — one per issue per repo (a repo may appear under
 * multiple kinds). `detail` is the verbatim `manifestError`/`lastSyncError` text
 * (null → rendered as an em-dash); `since` is the timestamp used for the default
 * most-recent-first sort.
 */
export interface SyncIssue {
  repoId: string;
  repoName: string;
  kind: SyncIssueKind;
  detail: string | null;
  since: string | null;
}

/** Manifest-adoption rollup derived from the unfiltered full list. */
export interface ManifestAdoption {
  /** Total listed (non-archived) repos. */
  total: number;
  /** Repos with a registered manifest (`autoIndexed: false`). */
  registered: number;
  /** Adoption percentage (0–100), or `null` when there are no repos. */
  adoptionPct: number | null;
}

const PER_PAGE = 100;

export const syncIssueKeys = {
  all: ['sync-issues'] as const,
  /** Raw full-list fetch, keyed by the server filter so filtered vs. unfiltered cache separately. */
  projects: (filter: QueryParams) => [...syncIssueKeys.all, 'projects', filter] as const,
};

/** Server-side boolean filter for a single kind; `{}` for auto_indexed / all-kinds. */
function serverFilterFor(kind: SyncIssueKind | undefined): QueryParams {
  switch (kind) {
    case 'broken_manifest':
      return { manifest_broken: true };
    case 'failed_sync':
      return { sync_failed: true };
    case 'stale':
      return { stale: true };
    // 'auto_indexed' is a per-item field (not a filter) and the all-kinds view
    // needs every repo — both fetch unfiltered.
    default:
      return {};
  }
}

/**
 * Fetch the full project list across pages (`per_page=100`). Bounded at ≤10
 * requests at AD-0001 §11.3's 1,000-repo ceiling; one request in the typical
 * fleet. Runs inside a TanStack Query `queryFn` so the result is cached.
 */
async function fetchAllProjects(filter: QueryParams): Promise<ProjectListItem[]> {
  const first = await apiGet<Paginated<ProjectListItem>>('/api/projects', {
    ...filter,
    per_page: PER_PAGE,
    page: 1,
  });
  const items = [...first.data];
  const totalPages = first.pagination.totalPages;
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await apiGet<Paginated<ProjectListItem>>('/api/projects', {
      ...filter,
      per_page: PER_PAGE,
      page,
    });
    items.push(...next.data);
  }
  return items;
}

/**
 * Derive issue rows from list items. When `kind` is set only that kind's rows
 * are emitted (the server has already narrowed the list for the filterable
 * kinds); otherwise every kind is emitted. Sorted most-recent-first by `since`,
 * with null timestamps last.
 */
export function deriveSyncIssues(
  items: ProjectListItem[],
  kind?: SyncIssueKind,
): SyncIssue[] {
  const rows: SyncIssue[] = [];
  for (const p of items) {
    if ((!kind || kind === 'broken_manifest') && p.manifestBroken) {
      rows.push({
        repoId: p.id,
        repoName: p.name,
        kind: 'broken_manifest',
        detail: p.manifestError,
        since: p.manifestBrokenAt,
      });
    }
    if ((!kind || kind === 'failed_sync') && p.lastSyncError !== null) {
      rows.push({
        repoId: p.id,
        repoName: p.name,
        kind: 'failed_sync',
        detail: p.lastSyncError,
        since: p.lastSyncedAt,
      });
    }
    if ((!kind || kind === 'stale') && p.stale) {
      rows.push({
        repoId: p.id,
        repoName: p.name,
        kind: 'stale',
        detail: null,
        since: p.lastSyncedAt,
      });
    }
    if ((!kind || kind === 'auto_indexed') && p.autoIndexed) {
      rows.push({
        repoId: p.id,
        repoName: p.name,
        kind: 'auto_indexed',
        detail: null,
        since: p.lastSyncedAt,
      });
    }
  }
  return sortBySinceDesc(rows);
}

/** Most-recent-first by `since`; null timestamps sort last. ISO strings compare lexically. */
function sortBySinceDesc(rows: SyncIssue[]): SyncIssue[] {
  return [...rows].sort((a, b) => {
    if (a.since === b.since) return 0;
    if (a.since === null) return 1;
    if (b.since === null) return -1;
    return b.since.localeCompare(a.since);
  });
}

/** Manifest adoption = share of listed repos with a registered manifest. */
export function deriveManifestAdoption(items: ProjectListItem[]): ManifestAdoption {
  const total = items.length;
  const registered = items.filter((p) => !p.autoIndexed).length;
  return {
    total,
    registered,
    adoptionPct: total === 0 ? null : Math.round((registered / total) * 100),
  };
}

/**
 * `useSyncIssues({ kind })` — the filterable issue list. Server-narrows for a
 * single filterable kind; fetches unfiltered (and derives client-side) for
 * `auto_indexed` and the default all-kinds view.
 */
export function useSyncIssues(
  { kind }: { kind?: SyncIssueKind } = {},
): UseQueryResult<SyncIssue[], Error> {
  const filter = serverFilterFor(kind);
  return useQuery({
    queryKey: syncIssueKeys.projects(filter),
    queryFn: () => fetchAllProjects(filter),
    select: (items) => deriveSyncIssues(items, kind),
  });
}

/**
 * `useManifestAdoption()` — the adoption-% stat tile. Always reads the unfiltered
 * full list, sharing its cache/fetch with the all-kinds `useSyncIssues` view.
 */
export function useManifestAdoption(): UseQueryResult<ManifestAdoption, Error> {
  return useQuery({
    queryKey: syncIssueKeys.projects({}),
    queryFn: () => fetchAllProjects({}),
    select: deriveManifestAdoption,
  });
}
